<template>
  <div v-if="editableItem">
    <div v-if="editingCopyrightCrud" class="backdrop-layer"></div>
    <!-- Modals -->
    <AdAssociationAdd
      :showDialogBtn="modals.code.open"
      :checkAssociationTypeSupport="false"
      :selectedAssociation="modals.code.selected"
      @cancelAddAssociation="cancelAddCode"
      @addAssociation="addCode"
    >
      <template #additionalFormItems="{item}">
        <EditableString
          v-model="item.canonicalUri"
          :label="$t('canonicalUri')"
          show-delete-icon
          :rules="rulesQuestionnaire['canonicalUri']"
        />
      </template>
    </AdAssociationAdd>

    <!-- cannonical URI -->
    <v-row dense>
      <v-col cols="8">
        <EditableString
          v-model="editableItem.canonicalUri"
          :label="$t('canonicalUri')"
          @change="updateString('canonicalUri')"
          :loading="loading.canonicalUri"
          show-delete-icon
          confirmation-required
          :rules="rulesQuestionnaire['canonicalUri']"
          :readonly="readonly"
        />
      </v-col>
    </v-row>
    <!-- official release date, expiration date -->
    <v-row dense>
      <v-col cols="4">
        <EditableDate
          v-model="officialReleaseDate"
          :label="$t('official-release-date')"
          @change="updateString('officialReleaseDate')"
          :loading="loading.officialReleaseDate"
          :disabled="loading.officialReleaseDate"
          :readonly="readonly"
          :clearable="true"
          @click:clear="
            officialReleaseDate = null
            updateString('officialReleaseDate', 'remove')
          "
        />
      </v-col>
      <v-col cols="4" class="pl-8">
        <EditableDate
          v-model="expirationDate"
          :label="$t('expiration-date')"
          @change="updateString('expirationDate')"
          :loading="loading.expirationDate"
          :disabled="loading.expirationDate"
          :readonly="readonly"
          :clearable="true"
          @click:clear="
            expirationDate = null
            updateString('expirationDate', 'remove')
          "
        />
      </v-col>
    </v-row>
    <!-- Code -->
    <v-row dense>
      <v-col>
        <v-card outlined class="mb-5">
          <AdCardTitle inline>
            {{ $t('code') }} ({{ $t('questionnaire') }})
            <template v-if="!readonly">
              <v-spacer></v-spacer>
              <AdButton
                type="add"
                :text="$t('code')"
                small
                :hint="$t('add-coded-concept')"
                @click="startAddCode"
              />
              <AdButton
                type="add"
                :text="$t('search')"
                small
                :hint="$t('terminology-browser')"
                @click="startTerminologyBrowser"
              />
            </template>
          </AdCardTitle>

          <v-card-text class="pa-0" v-if="Array.isArray(editableItem.code) && editableItem.code.length > 0">
            <v-data-table
              :items="editableItem.code"
              :headers="codeHeaders"
              :hide-default-footer="editableItem.code.length <= 5"
              :footer-props="{ 'items-per-page-options': [5,10] }"
            >
              <template #item.codeSystemName="{ item }">
                <div>{{ item.codeSystemName || item.codeSystem }}</div>
              </template>

              <template #item.actions="{ item }">
                <AdSpeedDial v-if="!readonly" direction="left" >
                  <AdSpeedOption
                    type="edit"
                    @click="startEditCode(item)"
                  />
                  <AdSpeedOption
                    type="delete"
                    @click="removeCode(item)"
                  />
                </AdSpeedDial>
              </template>
            </v-data-table>
          </v-card-text>

          <AdInfoOwl v-else inset>
            {{ !readonly ? $t('add-first-association') : $t('no-associations-yet') }}
          </AdInfoOwl>

        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <AdCopyrightCrud
          v-model="editableItem"
          :style="{ position: 'relative', zIndex: editingCopyrightCrud ? 3 : 1}"
          :showRouterlinkToPublishingAuthority="false"
          :enableAddCopyright="userIsEditor"
          :enableEditCopyright="userIsEditor"
          :enableRemoveCopyright="userIsEditor"
          :enableAddPublishingAuthority="userIsEditor"
          :enableEditPublishingAuthority="userIsEditor"
          :enableRemovePublishingAuthority="userIsEditor"
          :readonly="!editingCopyrightCrud || loading.copyrightCrud"
          :loading="loading.copyrightCrud"
          class="backdrop-item"
        >
          <template #header>
            <AdCardTitle inline>
              {{ $t('copyright-and-authorities') }}
              <v-spacer/>
              <AdButton type="edit" v-if="questionnaireItemIsEditable && !editingCopyrightCrud" @click="editingCopyrightCrud = true"/>
              <AdButton type="cancel" v-if="editingCopyrightCrud" @click="handleCancelCopyrightCrud"/>
              <AdButton type="save" v-if="editingCopyrightCrud & (copyrightChanged || publishingAuthorityChanged)" @click="handleSaveCopyrightCrud"/>
            </AdCardTitle>
          </template>
        </AdCopyrightCrud>
      </v-col>
    </v-row>
    <!-- Tags -->
    <v-row dense>
      <v-col cols="12" class="pt-2 pr-4">
        <v-combobox
          ref="tagInput"
          v-model="classificationTags"
          multiple
          chips
          deletable-chips
          dense
          :label="$t('tags')"
          :readonly="readonly"
          :disabled="loading.classification"
          :loading="loading.classification"
          @blur="handleBlurTagInput"
        >
          <template v-slot:append>
            <AdButton
              v-if="!readonly && classificationTagsChanged"
              icon="mdi-check"
              list
              noMarginRight
              @click="updateClassification('tag')"
            />
          </template>
        </v-combobox>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { cloneDeep, xor, uniqBy, xorWith, isEqual } from 'lodash'
import rulesQuestionnaire from '@/rules/questionnaire'
import AdAssociationAdd from '@/components/Utilities/AdAssociationAdd'
import AdCopyrightCrud from '@/components/Utilities/AdCopyrightCrud'

export default {
  name: 'QuestionnaireMetaInformation',
  components: { AdAssociationAdd, AdCopyrightCrud },
  props: {
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      editableItem: null,
      editingCopyrightCrud: false,
      loading: {
        canonicalUri: false,
        copyrightCrud: false,
        expirationDate: false,
        officialReleaseDate: false,
        classification: false
      },
      modals: {
        code: {
          open: false,
          selected: null
        }
      },
      rulesQuestionnaire
    }
  },
  computed: {
    ...mapGetters('questionnaire', [
      'questionnaireItem', 'questionnaireItemIsEditable'
    ]),

    classificationTags: {
      get () {
        return this.editableItem.classification?.[0]?.tag || []
      },
      set (value) {
        if (!this.editableItem.classification?.[0]) {
          this.$set(this.editableItem, 'classification', [{ tag: value }])
        } else if (!this.editableItem.classification[0].tag) {
          this.$set(this.editableItem.classification[0], 'tag', value)
        } else {
          this.editableItem.classification[0].tag = value
        }
      }
    },
    classificationTagsChanged () {
      const originalTags =
        this.questionnaireItem?.classification?.[0]?.tag || []
      const editedTags = this.classificationTags
      return !!xor(originalTags, editedTags).length
    },
    codeHeaders () {
      const headers = [
        { text: this.$t('code'), value: 'code', sortable: true },
        { text: this.$t('display-name'), value: 'displayName', sortable: true },
        { text: this.$t('codesystem'), value: 'codeSystemName', sortable: true },
        { text: this.$t('canonicalUri'), value: 'canonicalUri', sortable: true }
      ]
      if (this.userIsEditor) {
        headers.push({ value: 'actions', width: '15%', align: 'right', sortable: false })
      }
      return headers
    },
    contentUrl () {
      return this.questionnaireItem?.url
    },
    canonicalUriInSyncWithContentUrl () {
      return this.contentUrl === this.questionnaireItem?.canonicalUri
    },

    copyrightChanged () {
      const originalCopyrightData = this.questionnaireItem?.copyright?.map(copyright => ({ '#text': copyright['#text'], language: copyright.language })) || []
      const editedCopyrightData = this.editableItem?.copyright?.map(copyright => copyright['#text'] && { '#text': copyright['#text'], language: copyright.language }).filter(value => value) || []
      return !!xorWith(originalCopyrightData, editedCopyrightData, isEqual).length
    },
    publishingAuthorityChanged () {
      const originalPubAuthData = this.questionnaireItem?.publishingAuthority?.map(pa => ({ addrLine: pa.addrLine, id: pa.id, name: pa.name })) || []
      const editedPubAuthData = this.editableItem?.publishingAuthority?.map(pa => {
        const result = { addrLine: pa.addrLine, id: pa.id, name: pa.name }
        if (pa.op === 'remove') {
          result.op = 'remove'
        }
        return result
      }) || []

      return !!xorWith(originalPubAuthData, editedPubAuthData, isEqual).length
    },

    expirationDate: {
      get () {
        return this.editableItem.expirationDate?.split('T')?.[0] || null
      },
      set (value) {
        const dateTime = value === null ? null : `${value}T00:00:00`
        this.editableItem.expirationDate = dateTime
      }
    },
    officialReleaseDate: {
      get () {
        return this.editableItem.officialReleaseDate?.split('T')?.[0] || null
      },
      set (value) {
        const dateTime = value === null ? null : `${value}T00:00:00`
        this.editableItem.officialReleaseDate = dateTime
      }
    }
  },
  methods: {
    async addCode (code) {
      if (!Array.isArray(this.editableItem.code)) {
        this.$set(this.editableItem, 'code', [])
      }
      if (this.modals.code.selected === null && this.editableItem.code.some(item => item.code === code.code)) {
        return this.$oops(`${this.$t('already-referenced')} (${code.code})`)
      }
      if (this.modals.code.selected) {
        const index = this.editableItem.code.findIndex(item => item.code === code.code)
        if (index >= 0) {
          this.editableItem.code.splice(index, 1, code)
        }
      } else {
        this.editableItem.code.push(code)
      }

      await this.updateObject('code')
      this.cancelAddCode()
    },
    async cancelAddCode () {
      this.modals.code.selected = null
      this.modals.code.open = false
    },
    async handleBlurTagInput () {
      if (this.classificationTagsChanged && !this.loading.classification) {
        const agree = await this.$confirm(`${this.$t('save-changes')}?`)
        if (agree) {
          // Save changes
          return this.updateClassification('tag')
        }
        // Reset the tags on the input
        this.restoreEditableItem()
      }
    },
    async removeCode (code) {
      const agree = await this.$confirm(this.$t('are-you-sure-this-action-cannot-be-undone'))
      if (!agree) return

      try {
        const data = {
          parameter: [
            {
              op: 'remove',
              path: '/code',
              value: {
                code
              }
            }
          ]
        }
        this.loading.code = true
        await this.$store.dispatch('questionnaire/patchQuestionnaire', {
          item: this.editableItem,
          data
        })
      } catch (error) {
        this.restoreEditableItem()
        const errorMessage =
          error?.response?.data?.description ||
          error?.response?.statusText ||
          error.message ||
          'Error'
        this.$oops(errorMessage)
      }
      this.loading.code = false
    },
    handleCancelCopyrightCrud () {
      this.restoreEditableItem()
      this.editingCopyrightCrud = false
    },
    async handleSaveCopyrightCrud () {
      try {
        const data = { parameter: [] }
        // updates all languages of the copyright
        if (this.copyrightChanged && this.editableItem.copyright) {
          this.editableItem.copyright.forEach((copyright) => {
            if (!this.$filters.htmlHasTextContent(copyright['#text'])) {
              const copyrightToRemove = this.questionnaireItem?.copyright?.find(item => item.language === copyright.language)
              if (copyrightToRemove) {
                data.parameter.push({
                  op: 'remove',
                  path: '/copyright',
                  value: {
                    copyright: copyrightToRemove
                  }
                })
              }
            } else {
              data.parameter.push({
                op: 'replace',
                path: '/copyright',
                value: {
                  copyright
                }
              })
            }
          })
        }
        // Handle changes to publishingAuthority
        if (this.publishingAuthorityChanged && this.editableItem.publishingAuthority) {
          this.editableItem.publishingAuthority.filter((pa) => Object.prototype.hasOwnProperty.call(pa, 'op'))
            .forEach(pa => {
              data.parameter.push({
                op: pa.op,
                path: '/publishingAuthority',
                value: {
                  publishingAuthority: pa
                }
              })
            })
        }

        if (!data.parameter.length) {
          return this.handleCancelCopyrightCrud()
        }
        this.loading.copyrightCrud = true
        await this.$store.dispatch('questionnaire/patchQuestionnaire', { item: this.editableItem, data })
        this.$notify(this.$t('submission-update-success-label'), 'success')
        this.editingCopyrightCrud = false
      } catch (error) {
        this.restoreEditableItem()
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
      this.loading.copyrightCrud = false
    },
    restoreEditableItem () {
      this.$nextTick(() => {
        this.editableItem = cloneDeep(this.questionnaireItem)
      })
    },
    startAddCode () {
      this.modals.code.open = true
    },
    startEditCode (code) {
      this.modals.code.selected = code
      this.modals.code.open = true
    },
    async startTerminologyBrowser () {
      await this.$store.dispatch('dataset/changeActiveAssociationType', 'questionnaire')
      await this.$store.dispatch('terminology/setStartShopping', true)
      this.$router.push({ name: 'browser-project', params: { questionnairePanel: { questionnaireActive: this.editableItem, questionnaireItem: this.editableItem, applyToOverallQuestionnaire: true } } }).catch(() => {})
    },
    async updateClassification () {
      try {
        const data = { parameter: [] }
        const op = this.editableItem.classification?.some(item => item.tag?.length || Object.keys(item).length > 1) ? 'replace' : 'remove'
        data.parameter.push({
          op,
          path: '/classification',
          value: {
            classification: this.editableItem.classification
          }
        })

        this.loading.classification = true
        await this.$store.dispatch('questionnaire/patchQuestionnaire', {
          item: this.editableItem,
          data
        })
        this.$notify(this.$t('submission-update-success-label'), 'success')

        if (this.$refs.tagInput) {
          this.$refs.tagInput.blur()
        }
      } catch (error) {
        this.restoreEditableItem()
        const errorMessage =
          error?.response?.data?.description ||
          error?.response?.statusText ||
          error.message ||
          'Error'
        this.$oops(errorMessage)
      }
      this.loading.classification = false
    },

    // General method for patching (switch values)
    async updateSwitchValue (
      key,
      value,
      valueIfTrue = 'true',
      valueIfFalse = 'false'
    ) {
      try {
        const data = {
          parameter: [
            {
              op: 'replace',
              path: '/' + key,
              value: value === 'true' ? valueIfTrue : valueIfFalse
            }
          ]
        }
        if (this.loading[key] !== undefined) {
          this.loading[key] = true
        }
        await this.$store.dispatch('questionnaire/patchQuestionnaire', {
          item: this.editableItem,
          data
        })
        this.$notify(this.$t('submission-update-success-label'), 'success')
      } catch (error) {
        this.restoreEditableItem()
        const errorMessage =
          error?.response?.data?.description ||
          error?.response?.statusText ||
          'Error'
        this.$oops(errorMessage)
      }
      if (this.loading[key] !== undefined) {
        this.loading[key] = false
      }
    },
    // General method for patching string values
    async updateString (key, op = 'replace') {
      try {
        const data = {
          parameter: [
            {
              op: !this.editableItem[key] ? 'remove' : op,
              path: `/${key}`,
              value: this.editableItem[key]
            }
          ]
        }
        if (this.loading[key] !== undefined) {
          this.loading[key] = true
        }

        await this.$store.dispatch('questionnaire/patchQuestionnaire', {
          item: this.editableItem,
          data
        })
        this.$notify(this.$t('submission-update-success-label'), 'success')
      } catch (error) {
        this.restoreEditableItem()
        const errorMessage =
          error?.response?.data?.description ||
          error?.response?.statusText ||
          'Error'
        this.$oops(errorMessage)
      }
      if (this.loading[key] !== undefined) {
        this.loading[key] = false
      }
    },
    // General method for patching array & object values
    async updateObject (key) {
      try {
        const data = {
          parameter: []
        }
        if (Array.isArray(this.editableItem[key])) {
          this.editableItem[key].forEach((item) => {
            data.parameter.push({
              op: 'replace',
              path: `/${key}`,
              value: {
                [key]: item
              }
            })
          })
        } else if (typeof this.editableItem[key] === 'object') {
          data.parameter.push({
            op: 'replace',
            path: `/${key}`,
            value: {
              [key]: this.editableItem[key]
            }
          })
        } else {
          throw new Error(`editableItem.${key} is not an object`)
        }
        if (this.loading[key] !== undefined) {
          this.loading[key] = true
        }

        await this.$store.dispatch('questionnaire/patchQuestionnaire', {
          item: this.editableItem,
          data
        })
        this.$notify(this.$t('submission-update-success-label'), 'success')
      } catch (error) {
        this.restoreEditableItem()
        const errorMessage =
          error?.response?.data?.description ||
          error?.response?.statusText ||
          error.message ||
          'Error'
        this.$oops(errorMessage)
      }
      if (this.loading[key] !== undefined) {
        this.loading[key] = false
      }
    }
  },
  watch: {
    '$route.params': {
      immediate: true,
      handler (params) {
        if (params.questionnairePanel?.applyToOverallQuestionnaire && Array.isArray(params.codesFromTerminologyBrowser)) {
          this.editableItem = cloneDeep(params.questionnairePanel.questionnaireItem)
          const oldItems = this.editableItem.code || []

          this.editableItem.code = uniqBy([...oldItems, ...params.codesFromTerminologyBrowser], 'code')
          this.updateObject('code')
          delete this.$route.params.questionnairePanel
          delete this.$route.params.codesFromTerminologyBrowser
        }
      }
    },
    questionnaireItem: {
      immediate: true,
      deep: true,
      handler (newVal, oldVal) {
        this.editableItem = cloneDeep(newVal)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.backdrop-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 3;
}
</style>
